import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Area, Legend
} from 'recharts';
import {
  Download, TrendingUp, DollarSign, Calendar, FileText,
  Loader2, Ticket, ShoppingBag, BarChart2, List
} from 'lucide-react';
import { getReports } from '../services/adminService';

const MONTHS_MAP = { '1month': 1, '3months': 3, '6months': 6, '1year': 12 };
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMoney = (n) => `৳${fmt(n)}`;

const exportCsv = (filename, rows) => {
  const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff', fontSize: '12px' };

const EmptyChart = () => (
  <div className="h-[300px] flex items-center justify-center text-slate-500 text-sm">No data for selected period</div>
);

// ── Summary Card ────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardContent className="p-5">
      <div className="p-2 rounded-lg bg-slate-700/60 w-fit mb-3">{icon}</div>
      <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${accent || 'text-white'}`}>{value}</p>
      {sub && <p className="text-slate-500 text-xs mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

// ── Totals Bar for List View ────────────────────────────────────────────────
const TotalsBar = ({ items, type }) => {
  const totals = useMemo(() => {
    if (type === 'rooms') {
      const count = items.length;
      const revenue = items.reduce((s, b) => s + b.grandTotal, 0);
      const paid = items.reduce((s, b) => s + b.paidAmount, 0);
      const balance = items.reduce((s, b) => s + b.balance, 0);
      const discount = items.reduce((s, b) => s + (b.discountAmount || 0), 0);
      const nights = count > 0 ? (items.reduce((s, b) => s + b.nights, 0) / count).toFixed(1) : 0;
      return [
        { label: 'Bookings', value: count },
        { label: 'Net Revenue', value: fmtMoney(revenue), accent: 'text-green-400' },
        { label: 'Total Paid', value: fmtMoney(paid), accent: 'text-blue-400' },
        { label: 'Balance Due', value: fmtMoney(balance), accent: balance > 0 ? 'text-amber-400' : 'text-slate-400' },
        { label: discount > 0 ? `Discounts Given` : 'Avg. Stay', value: discount > 0 ? fmtMoney(discount) : `${nights} nights`, accent: discount > 0 ? 'text-red-400' : undefined },
      ];
    } else {
      const count = items.length;
      const revenue = items.reduce((s, o) => s + o.totalAmount, 0);
      const tickets = items.reduce((s, o) => s + o.totalTickets, 0);
      const paid = items.filter(o => o.paymentStatus === 'Paid').reduce((s, o) => s + o.totalAmount, 0);
      return [
        { label: 'Orders', value: count },
        { label: 'Total Tickets', value: tickets },
        { label: 'Total Revenue', value: fmtMoney(revenue), accent: 'text-green-400' },
        { label: 'Paid Revenue', value: fmtMoney(paid), accent: 'text-blue-400' },
        { label: 'Pending Revenue', value: fmtMoney(revenue - paid), accent: revenue - paid > 0 ? 'text-amber-400' : 'text-slate-400' },
      ];
    }
  }, [items, type]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
      {totals.map((t, i) => (
        <div key={i} className="bg-slate-700/50 border border-slate-600 rounded-xl px-4 py-3">
          <p className="text-slate-400 text-xs mb-1">{t.label}</p>
          <p className={`font-bold text-lg leading-tight ${t.accent || 'text-white'}`}>{t.value}</p>
        </div>
      ))}
    </div>
  );
};

// ── Th helper ───────────────────────────────────────────────────────────────
const Th = ({ children, right }) => (
  <th className={`px-3 py-2.5 text-xs font-semibold text-slate-300 bg-slate-700/80 border-b border-slate-600 whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);
const Td = ({ children, right, mono, accent }) => (
  <td className={`px-3 py-2 text-xs border-b border-slate-700/40 ${right ? 'text-right' : 'text-left'} ${mono ? 'font-mono' : ''} ${accent || 'text-slate-300'}`}>
    {children}
  </td>
);

const STATUS_COLOR = {
  Confirmed: 'bg-blue-600/80 text-blue-100',
  CheckedIn: 'bg-green-600/80 text-green-100',
  CheckedOut: 'bg-slate-500/80 text-slate-100',
  Hold: 'bg-yellow-600/80 text-yellow-100',
  NoShow: 'bg-orange-600/80 text-orange-100',
  Paid: 'bg-green-600/80 text-green-100',
  Pending: 'bg-amber-600/80 text-amber-100',
  Partial: 'bg-blue-500/80 text-blue-100',
};
const StatusPill = ({ v }) => (
  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_COLOR[v] || 'bg-slate-600 text-slate-200'}`}>{v}</span>
);

// ── Room Bookings Table ─────────────────────────────────────────────────────
const RoomBookingsTable = ({ bookings, onExport }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="flex flex-row items-center justify-between pb-3">
      <div>
        <CardTitle className="text-white text-base">Room Bookings</CardTitle>
        <CardDescription className="text-slate-400 text-xs mt-0.5">{bookings.length} record{bookings.length !== 1 ? 's' : ''}</CardDescription>
      </div>
      {bookings.length > 0 && (
        <Button variant="outline" size="sm" onClick={onExport} className="border-slate-600 text-slate-300 hover:bg-slate-700">
          <Download className="h-4 w-4 mr-1.5" />Export Excel
        </Button>
      )}
    </CardHeader>
    <CardContent className="p-0">
      {bookings.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-10">No bookings in this period</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr>
                <Th>#</Th>
                <Th>PNR</Th>
                <Th>Guest</Th>
                <Th>Phone</Th>
                <Th>Room Type</Th>
                <Th right>Rooms</Th>
                <Th>Check-In</Th>
                <Th>Check-Out</Th>
                <Th right>Nts</Th>
                <Th right>Sub Total</Th>
                <Th right>Tax</Th>
                <Th right>Svc</Th>
                <Th right>Discount</Th>
                <Th right>Grand Total</Th>
                <Th right>Paid</Th>
                <Th right>Balance</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th>Source</Th>
                <Th>Booked On</Th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => (
                <tr key={b.pnr} className={i % 2 === 0 ? 'bg-slate-800/30' : 'bg-slate-800/60'}>
                  <Td accent="text-slate-500">{i + 1}</Td>
                  <Td mono accent="text-blue-400">{b.pnr}</Td>
                  <Td>{b.guestName}</Td>
                  <Td accent="text-slate-400">{b.guestPhone || '—'}</Td>
                  <Td>{b.roomTypeName}</Td>
                  <Td right>{b.numberOfRooms}</Td>
                  <Td>{b.checkInDate}</Td>
                  <Td>{b.checkOutDate}</Td>
                  <Td right>{b.nights}</Td>
                  <Td right>{fmtMoney(b.subTotal)}</Td>
                  <Td right>{fmtMoney(b.taxAmount)}</Td>
                  <Td right>{fmtMoney(b.serviceCharge)}</Td>
                  <Td right accent={b.discountAmount > 0 ? 'text-red-400' : 'text-slate-500'}>{b.discountAmount > 0 ? `− ${fmtMoney(b.discountAmount)}` : '—'}</Td>
                  <Td right accent="text-white font-semibold">{fmtMoney(b.grandTotal)}</Td>
                  <Td right accent="text-green-400">{fmtMoney(b.paidAmount)}</Td>
                  <Td right accent={b.balance > 0 ? 'text-amber-400' : 'text-slate-400'}>{fmtMoney(b.balance)}</Td>
                  <Td><StatusPill v={b.status} /></Td>
                  <Td><StatusPill v={b.paymentStatus} /></Td>
                  <Td accent="text-slate-400">{b.source || 'Online'}</Td>
                  <Td accent="text-slate-400">{b.bookedAt}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

// ── Ticket Orders Table ─────────────────────────────────────────────────────
const TicketOrdersTable = ({ orders, onExport }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="flex flex-row items-center justify-between pb-3">
      <div>
        <CardTitle className="text-white text-base flex items-center gap-2">
          <Ticket className="h-4 w-4 text-amber-400" /> Ticket Orders
        </CardTitle>
        <CardDescription className="text-slate-400 text-xs mt-0.5">{orders.length} order{orders.length !== 1 ? 's' : ''}</CardDescription>
      </div>
      {orders.length > 0 && (
        <Button variant="outline" size="sm" onClick={onExport} className="border-slate-600 text-slate-300 hover:bg-slate-700">
          <Download className="h-4 w-4 mr-1.5" />Export Excel
        </Button>
      )}
    </CardHeader>
    <CardContent className="p-0">
      {orders.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-10">No ticket orders in this period</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Order Ref</Th>
                <Th>Lead Guest</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Tickets</Th>
                <Th right>Count</Th>
                <Th>Visit Date</Th>
                <Th>Booked On</Th>
                <Th right>Amount</Th>
                <Th>Payment</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={o.orderRef} className={i % 2 === 0 ? 'bg-slate-800/30' : 'bg-slate-800/60'}>
                  <Td accent="text-slate-500">{i + 1}</Td>
                  <Td mono accent="text-amber-400">{o.orderRef}</Td>
                  <Td>{o.leadName}</Td>
                  <Td accent="text-slate-400">{o.leadEmail}</Td>
                  <Td accent="text-slate-400">{o.leadPhone || '—'}</Td>
                  <Td accent="text-slate-300">{o.ticketSummary}</Td>
                  <Td right>{o.totalTickets}</Td>
                  <Td>{o.visitDate}</Td>
                  <Td accent="text-slate-400">{o.createdAt}</Td>
                  <Td right accent="text-white font-semibold">{fmtMoney(o.totalAmount)}</Td>
                  <Td><StatusPill v={o.paymentStatus} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

// ── Main Page ───────────────────────────────────────────────────────────────
const Reports = () => {
  const [view, setView] = useState('chart');        // 'chart' | 'list'
  const [listType, setListType] = useState('rooms'); // 'rooms' | 'tickets'
  const [reportType, setReportType] = useState('monthly');
  const [dateRange, setDateRange] = useState('6months');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getReports(reportType, MONTHS_MAP[dateRange] || 6);
      setData(result);
    } catch {
      toast({ title: 'Error', description: 'Failed to load report data.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [reportType, dateRange, toast]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const trend        = data?.trend || [];
  const sources      = data?.revenueBySource || [];
  const summary      = data?.summary || {};
  const ticketOrders = data?.ticketOrders || [];
  const roomBookings = data?.roomBookings || [];
  const isTicketEnabled = data?.isTicketEnabled ?? false;

  // Keep listType in sync if tickets get disabled
  useEffect(() => { if (!isTicketEnabled) setListType('rooms'); }, [isTicketEnabled]);

  const handleExportRooms = () => {
    const rows = [
      ['#','PNR','Guest Name','Phone','Email','Room Type','Rooms','Check-In','Check-Out','Nights','Sub Total','Tax','Service Charge','Discount','Grand Total','Paid','Balance','Status','Payment','Source','Booked On'],
      ...roomBookings.map((b, i) => [i+1, b.pnr, b.guestName, b.guestPhone||'', b.guestEmail||'', b.roomTypeName, b.numberOfRooms, b.checkInDate, b.checkOutDate, b.nights, b.subTotal, b.taxAmount, b.serviceCharge, b.discountAmount||0, b.grandTotal, b.paidAmount, b.balance, b.status, b.paymentStatus, b.source||'Online', b.bookedAt])
    ];
    exportCsv(`Room_Bookings_${dateRange}.csv`, rows);
    toast({ title: 'Exported', description: 'Room bookings exported as CSV.' });
  };

  const handleExportTickets = () => {
    const rows = [
      ['#','Order Ref','Lead Name','Email','Phone','Tickets','Count','Visit Date','Booked On','Amount','Payment'],
      ...ticketOrders.map((o, i) => [i+1, o.orderRef, o.leadName, o.leadEmail, o.leadPhone||'', o.ticketSummary, o.totalTickets, o.visitDate, o.createdAt, o.totalAmount, o.paymentStatus])
    ];
    exportCsv(`Ticket_Orders_${dateRange}.csv`, rows);
    toast({ title: 'Exported', description: 'Ticket orders exported as CSV.' });
  };

  const handleExportChart = (name) => {
    if (['Revenue Trend', 'Booking Analysis', 'Occupancy Rate'].includes(name)) {
      const rows = [['Period','Room Revenue (৳)','Ticket Revenue (৳)','Bookings','Occupancy (%)'], ...trend.map(r => [r.period, r.revenue, r.ticketRevenue, r.bookings, r.occupancy])];
      exportCsv(`${name.replace(/ /g,'_')}.csv`, rows);
    } else if (name === 'Revenue by Source') {
      const rows = [['Source','Amount (৳)','Percentage (%)'], ...sources.map(s => [s.name, s.amount, s.value])];
      exportCsv('Revenue_by_Source.csv', rows);
    }
    toast({ title: 'Exported', description: `${name} exported as CSV.` });
  };

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Sales & Revenue Reports</h1>
          <p className="text-slate-400 mt-1">Analyze hotel and ticket performance</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* View toggle */}
          <div className="flex items-center bg-slate-800 border border-slate-700 rounded-xl p-1 gap-1">
            <button
              onClick={() => setView('chart')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${view === 'chart' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
            >
              <BarChart2 className="h-4 w-4" /> By Chart
            </button>
            <button
              onClick={() => setView('list')}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${view === 'list' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
            >
              <List className="h-4 w-4" /> By List
            </button>
          </div>

          {/* Chart-only: period selector */}
          {view === 'chart' && (
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Date range — always shown */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="1month">1 Month</SelectItem>
              <SelectItem value="3months">3 Months</SelectItem>
              <SelectItem value="6months">6 Months</SelectItem>
              <SelectItem value="1year">1 Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Summary Cards (always visible) ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <SummaryCard icon={<DollarSign className="h-5 w-5 text-green-400" />} label="Total Revenue" value={loading ? '—' : fmtMoney(summary.totalRevenue)} sub="Rooms + Tickets" accent="text-green-400" />
        <SummaryCard icon={<Calendar className="h-5 w-5 text-blue-400" />} label="Room Bookings" value={loading ? '—' : (summary.totalBookings ?? '—')} />
        <SummaryCard icon={<TrendingUp className="h-5 w-5 text-purple-400" />} label="Avg. Daily Rate" value={loading ? '—' : fmtMoney(summary.avgDailyRate)} />
        <SummaryCard icon={<FileText className="h-5 w-5 text-orange-400" />} label="Occupancy Rate" value={loading ? '—' : `${summary.occupancyRate ?? 0}%`} />
        <SummaryCard icon={<Ticket className="h-5 w-5 text-amber-400" />} label="Ticket Revenue" value={loading ? '—' : fmtMoney(summary.ticketRevenue)} accent="text-amber-400" />
        <SummaryCard icon={<ShoppingBag className="h-5 w-5 text-pink-400" />} label="Ticket Orders" value={loading ? '—' : (summary.ticketOrderCount ?? 0)} />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
        </div>
      )}

      {!loading && view === 'chart' && (
        <>
          {/* ── Row 1: Revenue Trend + Booking Analysis ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-white">Revenue Trend</CardTitle>
                  <CardDescription className="text-slate-400 text-xs mt-0.5">Rooms (area) + Tickets (line)</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleExportChart('Revenue Trend')} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <Download className="h-4 w-4 mr-1" />CSV
                </Button>
              </CardHeader>
              <CardContent>
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={trend}>
                      <defs>
                        <linearGradient id="roomGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                      <XAxis dataKey="period" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={v => `৳${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtMoney(v), n === 'revenue' ? 'Room Revenue' : 'Ticket Revenue']} />
                      <Legend formatter={v => v === 'revenue' ? 'Room Revenue' : 'Ticket Revenue'} wrapperStyle={{ fontSize: '12px', color: '#94a3b8' }} />
                      <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="url(#roomGrad)" strokeWidth={2} />
                      <Line type="monotone" dataKey="ticketRevenue" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} activeDot={{ r: 5 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-white">Booking Analysis</CardTitle>
                  <CardDescription className="text-slate-400 text-xs mt-0.5">Room booking count trends</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleExportChart('Booking Analysis')} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <Download className="h-4 w-4 mr-1" />CSV
                </Button>
              </CardHeader>
              <CardContent>
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                      <XAxis dataKey="period" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Line type="monotone" dataKey="bookings" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </CardContent>
            </Card>
          </div>

          {/* ── Row 2: Revenue by Source + Occupancy ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-white">Revenue by Source</CardTitle>
                  <CardDescription className="text-slate-400 text-xs mt-0.5">Rooms & tickets by channel</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleExportChart('Revenue by Source')} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <Download className="h-4 w-4 mr-1" />CSV
                </Button>
              </CardHeader>
              <CardContent>
                {sources.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={210}>
                      <PieChart>
                        <Pie data={sources} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={4} dataKey="value">
                          {sources.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip formatter={(v, n, p) => [fmtMoney(p.payload.amount) + ` (${v}%)`, p.payload.name]} contentStyle={tooltipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 space-y-2">
                      {sources.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                            <span className="text-slate-300">{s.name}</span>
                          </div>
                          <span className="text-white font-medium">{fmtMoney(s.amount)} <span className="text-slate-500">({s.value}%)</span></span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <EmptyChart />}
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-white">Occupancy Rate Trend</CardTitle>
                  <CardDescription className="text-slate-400 text-xs mt-0.5">Room occupancy over time</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleExportChart('Occupancy Rate')} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <Download className="h-4 w-4 mr-1" />CSV
                </Button>
              </CardHeader>
              <CardContent>
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                      <XAxis dataKey="period" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                      <Tooltip contentStyle={tooltipStyle} formatter={v => [`${v}%`, 'Occupancy']} />
                      <Bar dataKey="occupancy" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {!loading && view === 'list' && (
        <div className="space-y-4">
          {/* List type toggle (only if tickets enabled) */}
          {isTicketEnabled && (
            <div className="flex items-center gap-4 bg-slate-800/50 border border-slate-700 rounded-xl px-5 py-4">
              <span className="text-slate-400 text-sm font-medium">Show:</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="listType"
                  value="rooms"
                  checked={listType === 'rooms'}
                  onChange={() => setListType('rooms')}
                  className="accent-blue-500 w-4 h-4"
                />
                <span className={`text-sm font-medium ${listType === 'rooms' ? 'text-white' : 'text-slate-400'}`}>
                  Room Bookings <span className="text-slate-500 font-normal">({roomBookings.length})</span>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="listType"
                  value="tickets"
                  checked={listType === 'tickets'}
                  onChange={() => setListType('tickets')}
                  className="accent-amber-500 w-4 h-4"
                />
                <span className={`text-sm font-medium ${listType === 'tickets' ? 'text-white' : 'text-slate-400'}`}>
                  Ticket Orders <span className="text-slate-500 font-normal">({ticketOrders.length})</span>
                </span>
              </label>
            </div>
          )}

          {/* Totals summary */}
          <TotalsBar
            items={listType === 'rooms' ? roomBookings : ticketOrders}
            type={listType}
          />

          {/* Table */}
          {listType === 'rooms'
            ? <RoomBookingsTable bookings={roomBookings} onExport={handleExportRooms} />
            : <TicketOrdersTable orders={ticketOrders} onExport={handleExportTickets} />
          }
        </div>
      )}
    </div>
  );
};

export default Reports;
